'use client';

import { PropsWithChildren, useContext, useState } from 'react';

import { CheckoutModal } from '../CheckoutModal/CheckoutModal';
import { OptimizelyContext } from '@optimizely/react-sdk';
import { OptimizelyContextInterface } from '@optimizely/react-sdk/dist/Context';
import classNames from 'classnames';
import { isLightColor } from '../../utils/colors';
import styles from './DonateButton.module.scss';
import { usePage } from '../Providers/PageProvider';
import { useTranslation } from 'react-i18next';

type DonateButtonProps = {
  id?: string;
  className?: string;
  colorVariant?: 'brand' | 'primary';
  as?: React.ElementType;
  onClick: (event: React.MouseEvent) => void;
  checkoutMode: 'modal' | 'popup' | 'navigate';
} & PropsWithChildren;

export const DonateButton: React.FC<DonateButtonProps> = ({
  id,
  children,
  className,
  colorVariant = 'primary',
  as = 'a',
  checkoutMode,
  onClick,
}) => {
  const { t } = useTranslation();
  const { url, status, branding } = usePage();
  const isDonateActive = status === 'ACTIVE';
  const isPrimaryBrandColorLight = branding.primaryColor
    ? isLightColor(branding.primaryColor)
    : false;
  const isButton = as === 'button';
  const Element = as;

  const { optimizely } =
    useContext<OptimizelyContextInterface>(OptimizelyContext);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  const clickHandler = (event: React.MouseEvent) => {
    let decision;

    if (checkoutMode === 'navigate' || checkoutMode === 'popup') {
      decision = optimizely?.decide(
        'consumerfintech_jgcheckout_conversion_modal_to_redirect_f42',
      );
    }
    if (decision?.enabled) {
      event.preventDefault();
      setIsCheckoutModalOpen(true);
    } else {
      onClick(event);
    }
  };

  if (!isDonateActive) {
    return (
      <>
        <div className={classNames('cp-btn w-full', styles.disabled)}>
          <span aria-hidden>{children}</span>
          <span className="cp-sr-only">{t('common.donateButtonInactive')}</span>
        </div>
      </>
    );
  }

  return (
    <>
      <Element
        className={classNames(
          'cp-btn w-full branded-text-neutral',
          {
            ['cp-btn-primary']:
              colorVariant === 'primary' && !isPrimaryBrandColorLight,
            ['cp-btn-brand']:
              colorVariant === 'brand' && !isPrimaryBrandColorLight,
            ['cp-btn-secondary']: isPrimaryBrandColorLight,
          },
          className,
        )}
        href={!isButton ? `${url}/donate?startedBy=webjustgiving` : undefined}
        target={!isButton ? '_blank' : undefined}
        onClick={clickHandler}
        id={id}
      >
        {children}
      </Element>
      {isCheckoutModalOpen && (
        <CheckoutModal
          isOpen={isCheckoutModalOpen}
          setIsOpen={setIsCheckoutModalOpen}
          isPrimaryBrandColorLight={isPrimaryBrandColorLight}
        />
      )}
    </>
  );
};
